/**
 * SEO helpers — shared logic for deciding whether a page should be indexable.
 *
 * `isThinProduct` flags affiliate product detail pages that lack enough unique
 * editorial content to be worth indexing (Google flags these as
 * "Crawled - currently not indexed"). Such pages still render and remain
 * crawlable (we use noindex,follow), but are excluded from the sitemap and
 * carry a robots noindex meta tag.
 */
export interface ThinProductLike {
  description?: string | null;
  pros?: string[] | null;
  cons?: string[] | null;
  key_features?: string[] | null;
  expert_review_url?: string | null;
  rating?: number | null;
  best_for?: string | null;
}

export function isThinProduct(p: ThinProductLike | null | undefined): boolean {
  if (!p) return true;
  const descLen = (p.description ?? "").trim().length;
  // Strong signal: an editor-written review URL means we have a real review.
  if (p.expert_review_url && p.expert_review_url.trim().length > 0) return false;
  // Long description alone is enough.
  if (descLen >= 250) return false;
  // Otherwise require structured editorial content.
  const pros = p.pros?.length ?? 0;
  const cons = p.cons?.length ?? 0;
  const features = p.key_features?.length ?? 0;
  const hasRating = !!p.rating;
  if (descLen >= 120 && pros >= 2 && cons >= 1) return false;
  if (descLen >= 120 && features >= 3 && hasRating) return false;
  return true;
}

/**
 * Safely serialise an object to a JSON-LD string.
 *
 * Guards against Google Search Console "Unparsable structured data" errors
 * (e.g. "Bad escape sequence in string") by:
 *  - stripping control characters and lone surrogates that break JSON parsers
 *  - dropping undefined values
 *  - escaping "</script" so the block can't terminate early
 */
export function jsonLd(schema: unknown): string {
  const clean = (value: unknown): unknown => {
    if (typeof value === "string") {
      return value
        // remove control chars (except tab/newline, which JSON.stringify escapes)
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, "")
        // remove unpaired surrogates
        .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])/g, "")
        .replace(/(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "")
        .trim();
    }
    if (Array.isArray(value)) return value.map(clean).filter((v) => v !== undefined);
    if (value && typeof value === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        const c = clean(v);
        if (c !== undefined) out[k] = c;
      }
      return out;
    }
    return value;
  };

  return JSON.stringify(clean(schema)).replace(/<\/script/gi, "<\\/script");
}

const TITLE_MAX = 60;
const BRAND_SUFFIX = " | Garden-Review";

/** @deprecated kept for callers; titles are no longer truncated. Trim a string to a maximum length on a word boundary, without ellipsis. */
export function trimToLength(text: string, max: number): string {
  const clean = text.replace(/\s*[…]+\s*$/, "").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut)
    .replace(/[\s\-–—:,|]+$/, "")
    .trim();
}

/**
 * Build a search-friendly <title> that stays within Google's ~60 character
 * display limit. The brand suffix is only appended when it fits.
 */
export function buildSeoTitle(rawTitle: string, max = TITLE_MAX): string {
  const base = (rawTitle || "Garden-Review").replace(/\s+/g, " ").trim();
  const withBrand = `${base}${BRAND_SUFFIX}`;
  if (withBrand.length <= max) return withBrand;
  // Never truncate: search engines handle long titles themselves.
  return base;
}
